import React, { useMemo, useState } from 'react';
import { Box, Flex, Spinner, Text, Tooltip } from '@chakra-ui/react';
import {
  Modal,
  Button,
  Badge,
  Input,
  ButtonText,
  ButtonIcon,
  FormFeedback
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';
import { IAuthorWithContribution } from '@publishing-engine/domain/models';
import { ContributionsBadge } from 'src/components/Submission/ContributionsBadge';
import { FieldLabel } from 'src/components/Field';
import {
  faInfoCircle,
  faPencil,
  faPlus
} from '@fortawesome/pro-regular-svg-icons';
import ContributionModal from './ContributionModal';
import AffiliationModal from './AffiliationModal';
import { useValidateORCIDMutation } from 'src/api/signUpMutation';
import { validateOrcidFormat } from 'src/utils/orcIdValidator';
import { ISubmissionAuthor } from 'src/contexts/FormContext';
import { FormFieldAuthors } from '@publishing-engine/domain/types/article';
import { IAuthorRolesMap } from 'src/utils/groupAuthorByRoles';
import { emailRegex } from '@publishing-engine/domain/validation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApply: (data: IAuthorWithContribution) => void;
  author: IAuthorWithContribution | undefined;
  index: number;
  customSetValues: any;
  authors: ISubmissionAuthor[];
  config: FormFieldAuthors | null;
  authorRoleMap: IAuthorRolesMap;
}

const EditAuthorInformation: React.FC<Props> = ({
  isOpen,
  onClose,
  onApply,
  author,
  index,
  customSetValues,
  authors,
  config,
  authorRoleMap
}) => {
  const {
    register,
    getValues,
    setValue,
    reset,
    clearErrors,
    formState: { errors },
    setError,
    trigger,
    watch
  } = useForm<IAuthorWithContribution>({
    defaultValues: author,
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { validations } = config?.params || {};
  const { orcidField, affiliationField, contributionsField } =
    validations || {};

  const contributionDegreeField =
    validations && 'contributionDegreeField' in validations
      ? validations.contributionDegreeField
      : 'hidden';

  const [isContributionModalOpen, setContributionModalOpen] = useState(false);
  const [isAffiliationModalOpen, setAffiliationModalOpen] = useState(false);
  const validateORCIDMutation = useValidateORCIDMutation();
  const { affiliation, country, countryCode, city, postalCode, ringgoldId } =
    author ?? {};
  const [isValidOrcid, setIsValidOrcid] = useState(false);
  const initialAuthorData = useMemo(
    () => ({
      affiliation,
      country,
      countryCode,
      city,
      postalCode,
      ringgoldId
    }),
    []
  );
  const handleApplyContributions = (data: any) => {
    if (index !== null) {
      setValue(`contributions`, data);
    }
    setContributionModalOpen(false);
  };

  const handleClose = () => {
    customSetValues(`metadata.authors.${index}`, {
      ...author,
      ...initialAuthorData
    });
    onClose();
    reset();
  };
  const { contributions } = getValues();

  const authorTypeText = (['Submitting', 'Corresponding'] as const).reduce(
    (acc, authorType) => {
      if (authorRoleMap[authorType] === author?.email) {
        return `${acc} ${acc ? '&' : ''} ${authorType.replace(' Author', '')}`;
      }
      return acc;
    },
    ''
  );

  const isNewAuthor = !author?.email;

  const orcidId = watch('orcid');
  const email = getValues('email');

  const isLoading = validateORCIDMutation.isPending;

  const validateOrcidId = async () => {
    const duplicate = authors.some(
      (a, i) => i !== index && a.orcid === orcidId && orcidId?.trim()
    );

    if (duplicate) {
      setError('orcid', {
        type: 'manual',
        message: 'This ORCID is already used by another author.'
      });

      setIsValidOrcid(false);
      return false;
    }

    if (orcidId) {
      clearErrors('orcid');
      const { isValid, message } = validateOrcidFormat(orcidId);
      if (!isValid) {
        setIsValidOrcid(false);
        setError('orcid', {
          type: 'manual',
          message: message
        });
        return;
      }
      const response = await validateORCIDMutation.mutateAsync({
        orcid: orcidId
      });

      if (!response?.data?.isValidOrcid) {
        setError('orcid', {
          type: 'manual',
          message: response?.message?.message || 'Invalid ORCID'
        });

        setIsValidOrcid(false);
        return false;
      } else {
        setIsValidOrcid(true);
      }
    }

    return true;
  };

  const validateAffiliation = (affiliation: string) => {
    if (affiliationField === 'mandatory' && !affiliation) {
      setError('affiliation', {
        type: 'manual',
        message: 'Affiliation is required'
      });

      return false;
    }

    return true;
  };

  const validateEmail = (email: string) => {
    if (!email?.trim()) {
      return true;
    }

    const duplicate = authors.some((a, i) => {
      const baseCondition = a.email === email;

      if (!isNewAuthor) {
        return baseCondition && i !== index;
      }

      return baseCondition;
    });

    if (duplicate) {
      setError('email', {
        type: 'manual',
        message: 'This email is already used by another author'
      });

      return false;
    }

    return true;
  };
  function hasNumber(value: string): boolean {
    return /\d/.test(value);
  }
  const onSubmit = async (formData: IAuthorWithContribution) => {
    const isValid = await trigger();
    const isValidOrcidId = await validateOrcidId();
    const isAffiliationValid = validateAffiliation(formData.affiliation?.trim() ?? '');
    const isEmailValid = validateEmail(formData.email);

    if (!isValid || !isValidOrcidId || !isAffiliationValid || !isEmailValid) return;

    onApply(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="755px">
      <button></button>
      <Flex flexDirection="column" gap={6} padding={8}>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">
            {isNewAuthor ? 'Add' : 'Edit'} Author Information
          </Text>
          <Flex gap={4}>
            {Object.keys(authorRoleMap).map(role => {
              if (authorRoleMap[role as keyof IAuthorRolesMap] === email) {
                return (
                  <Badge variant="attention" size="context">
                    {role} Author
                  </Badge>
                );
              }
            })}
          </Flex>
        </Flex>

        <Flex justifyContent="space-between" gap={4}>
          <Flex flexDirection="column" flex={1} gap={2}>
            <FieldLabel label={'First Name'} subText={''} isRequired />
            <Tooltip
              hasArrow
              borderRadius={4}
              placement="top"
              label={
                authorTypeText
                  ? `Editing this First Name is not possible since the author is a ${authorTypeText} author.`
                  : ''
              }
            >
              <Input
                placeholder="Enter first name"
                {...register('firstName', {
                  required: 'First name is required',
                  validate: value =>
                    !hasNumber(value) || 'First name must not contain numbers'
                })}
                isInvalid={!!errors?.firstName}
                disabled={!!authorTypeText}
              />
              {errors?.firstName && (
                <FormFeedback variant="error">
                  {errors.firstName?.message}
                </FormFeedback>
              )}
            </Tooltip>
          </Flex>
          <Flex flexDirection="column" flex={1} gap={2}>
            <FieldLabel label={'Middle Name'} subText={''} isRequired={false} />
            <Tooltip
              hasArrow
              borderRadius={4}
              placement="top"
              label={
                authorTypeText
                  ? `Editing this Middle Name is not possible since the author is a ${authorTypeText} author.`
                  : ''
              }
            >
              <Input
                placeholder="Enter middle name"
                {...register('middleName', {
                  validate: value => {
                    if (!value?.trim()) {
                      clearErrors('middleName');
                      return true;
                    }
                    return (
                      !hasNumber(value) ||
                      'Middle name must not contain numbers'
                    );
                  }
                })}
                disabled={!!authorTypeText}
              />
              {errors?.middleName && (
                <FormFeedback variant="error">
                  {errors.middleName?.message}
                </FormFeedback>
              )}
            </Tooltip>
          </Flex>
          <Flex flexDirection="column" flex={1} gap={2}>
            <FieldLabel label={'Last Name'} subText={''} isRequired />
            <Tooltip
              hasArrow
              borderRadius={4}
              placement="top"
              label={
                authorTypeText
                  ? `Editing this Last Name is not possible since the author is a ${authorTypeText} author.`
                  : ''
              }
            >
              <Input
                placeholder="Enter last name"
                {...register('lastName', {
                  required: 'Last name is required',
                  validate: value =>
                    !hasNumber(value) || 'Last name must not contain numbers'
                })}
                isInvalid={!!errors?.lastName}
                disabled={!!authorTypeText}
              />
              {errors?.lastName && (
                <FormFeedback variant="error">
                  {errors.lastName?.message}
                </FormFeedback>
              )}
            </Tooltip>
          </Flex>
        </Flex>

        <Flex flexDirection="column" flex={1} gap={2}>
          <FieldLabel label={'Email'} subText={''} isRequired />
          <Tooltip
            hasArrow
            borderRadius={4}
            placement="top"
            label={
              authorTypeText
                ? `Editing this Email is not possible since the author is a ${authorTypeText} author.`
                : ''
            }
          >
            <Input
              type="email"
              placeholder="e.g., fabius@molecularconnections.com"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: emailRegex,
                  message: 'Please enter a valid email address'
                }
              })}
              isInvalid={!!errors?.email}
              disabled={!!authorTypeText}
            />
            {errors?.email && (
              <FormFeedback variant="error">
                {errors.email?.message}
              </FormFeedback>
            )}
          </Tooltip>
        </Flex>

        {orcidField !== 'hidden' && (
          <Tooltip
            hasArrow
            borderRadius={4}
            placement="top"
            label={
              authorTypeText
                ? `Editing this ORCID is not possible since the author is a ${authorTypeText} author.`
                : ''
            }
          >
            <Flex flexDirection="column" flex={1} gap={2}>
              <FieldLabel
                label={'ORCID iD'}
                subText={''}
                isRequired={orcidField === 'mandatory'}
              />
              <Input
                value={orcidId}
                placeholder="Enter the author's ORCID iD"
                {...register('orcid', {
                  ...(orcidField === 'mandatory' && {
                    required: 'ORCID iD is required'
                  }),
                  setValueAs: value => value.trim()
                })}
                isInvalid={!!errors?.orcid}
                onBlur={() => validateOrcidId()}
                disabled={!!authorTypeText}
              />
              <Flex>
                {isLoading && (
                  <Flex gap={1} alignItems={'center'}>
                    <Spinner size="sm" />
                    <Text>Validating ORCID</Text>
                  </Flex>
                )}
                {errors?.orcid && (
                  <FormFeedback variant="error">
                    {errors.orcid?.message}
                  </FormFeedback>
                )}
                {!isLoading && orcidId && isValidOrcid && (
                  <Flex alignItems={'center'} gap={1}>
                    <FormFeedback variant="success">
                      ORCID successfully verified
                    </FormFeedback>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Tooltip>
        )}

        {affiliationField !== 'hidden' && (
          <Flex flexDirection="column" flex={1} gap={2}>
            <FieldLabel
              label={'Affiliation'}
              subText={''}
              isRequired={affiliationField === 'mandatory'}
            />
            <Flex flexDirection="row" alignItems="center" gap={2}>
              {author?.affiliation ? (
                <>
                  <Text>
                    {author.affiliation}
                    {author.city && `, ${author.city}`}
                    {author.country && `, ${author.country}`}
                    {author.postalCode && `, ${author.postalCode}`}
                  </Text>
                  <Tooltip
                    hasArrow
                    borderRadius={4}
                    placement="top"
                    label={
                      authorTypeText
                        ? `Editing this Affiliation is not possible since the author is a ${authorTypeText} author.`
                        : ''
                    }
                  >
                    <Box>
                      <ButtonIcon
                        ariaLabel={''}
                        icon={<FontAwesomeIcon icon={faPencil} />}
                        onClick={() => setAffiliationModalOpen(true)}
                        disabled={!!authorTypeText}
                      />
                    </Box>
                  </Tooltip>
                </>
              ) : (
                <Box>
                  <ButtonText
                    leftIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => setAffiliationModalOpen(true)}
                    data-cy={`add-contributions-button-${index}`}
                  >
                    Add Affiliation
                  </ButtonText>
                </Box>
              )}
            </Flex>
            {errors.affiliation && (
              <FormFeedback variant="error">
                {errors.affiliation?.message}
              </FormFeedback>
            )}
          </Flex>
        )}

        {contributionsField !== 'hidden' && (
          <Flex flexDirection="column" flex={1} gap={2}>
            <Flex>
              <FieldLabel
                label={'CRediT Contributions'}
                subText={''}
                isRequired={contributionsField === 'mandatory'}
              />
              <Tooltip
                hasArrow
                shouldWrapChildren
                placement="top"
                label="Contributor Role Taxonomy (CRediT) Contributions"
                borderRadius={4}
                data-cy="contributions-info-tooltip"
              >
                <ButtonIcon
                  size="small"
                  ariaLabel="Back to workspace settings"
                  icon={<FontAwesomeIcon icon={faInfoCircle} />}
                  data-cy="contributions-info-icon"
                />
              </Tooltip>
            </Flex>
            {contributions?.length ? (
              <Flex alignItems="center" flexDirection="row">
                <ContributionsBadge contributions={contributions} />
                <ButtonIcon
                  ariaLabel={''}
                  icon={<FontAwesomeIcon icon={faPencil} />}
                  onClick={() => setContributionModalOpen(true)}
                />
              </Flex>
            ) : (
              <Box>
                <ButtonText
                  leftIcon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={() => setContributionModalOpen(true)}
                  data-cy={`add-contributions-button-${index}`}
                >
                  Add Contributions
                </ButtonText>
              </Box>
            )}
          </Flex>
        )}

        <Flex flexDirection="row" gap={2} justifyContent="end">
          <Button
            variant="secondary"
            onClick={handleClose}
            data-cy="cancel-button"
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => onSubmit(getValues())}
            data-cy="apply-button"
          >
            Save Changes
          </Button>
        </Flex>
      </Flex>

      <ContributionModal
        isOpen={isContributionModalOpen}
        onClose={() => setContributionModalOpen(false)}
        onApply={handleApplyContributions}
        defaultContributions={getValues(`contributions`) ?? []}
        data-cy="contribution-modal"
        contributionDegreeField={contributionDegreeField}
      />

      {isAffiliationModalOpen && (
        <AffiliationModal
          isOpen={isAffiliationModalOpen}
          onClose={() => setAffiliationModalOpen(false)}
          index={index ?? 0}
          onAdd={(v: string) => {
            clearErrors('affiliation');
            setValue('affiliation', v);
          }}
        />
      )}
    </Modal>
  );
};

export default EditAuthorInformation;
